import React, { useRef, useEffect } from "react";

// MUI
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

// Components
import { BasicInfoSection } from "./BasicInfoSection";
import { BidDetailsSection } from "./BidDetailsSection";
import { AdditionalDetailsSection } from "./AdditionalDetailsSection";
import WinProbabilityDetails from "../win-probability-details";
import CustomerPOCInfoSection from "./CustomerPOCInfoSection";
import Divider from "../../Divider";

// Hooks
import { useAppSelector } from "../../../redux/hooks/redux-hooks";

// Types
import { ProjectSPModel } from "../../../models/ProjectSPModel";

interface IBidDetailsLayoutSection {
  data: ProjectSPModel;
  isSideMenuExpanded: boolean;
  handleSideMenuToggle: () => void;
}

const HamburgerSvg = (
  <svg
    style={{ height: "24px", width: "24px" }}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"
    />
  </svg>
);

const verticalHamburger = (
  <svg
    style={{ height: "24px", width: "24px", transform: "rotate(90deg)" }}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"
    />
  </svg>
);

const BidDetailsLayoutSection = ({
  handleSideMenuToggle,
  isSideMenuExpanded,
  data,
}: IBidDetailsLayoutSection) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // To calculate the bid details section maxHeight based on login user
  const loginRoleId = useAppSelector(
    (state) => state.user.loggedIn_User.roleId
  );

  // Scroll to top on data change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scroll({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [data]);

  return (
    <Card
      ref={containerRef}
      sx={{
        margin: "5px",
        padding: "0 10px 10px 10px",
        maxHeight: `calc(100vh - ${
          loginRoleId === 1 || loginRoleId === 2 ? 220 : 185
        }px)`,
        overflowY: "auto",
      }}
    >
      <CardContent sx={{ padding: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Bid Details</Typography>
          <IconButton
            aria-label="bid details"
            onClick={handleSideMenuToggle}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {isSideMenuExpanded ? HamburgerSvg : verticalHamburger}
          </IconButton>
        </Stack>
        <Divider />
        <div style={{ paddingLeft: "1rem" }}>
          <BasicInfoSection data={data} />
          <BidDetailsSection data={data} />
          <AdditionalDetailsSection data={data} />
        </div>
        <CustomerPOCInfoSection />
        <WinProbabilityDetails />
      </CardContent>
    </Card>
  );
};

export default BidDetailsLayoutSection;
